import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATS = [
  { v: "sales", l: "Sales-related" },
  { v: "service", l: "Service-related" },
  { v: "both", l: "Both / Common" },
];

export function BranchDealerMapping() {
  const [branches, setBranches] = useState([]);
  const [dealers, setDealers] = useState([]);

  const load = useCallback(() => {
    api.get("/hr/branches").then(({ data }) => setBranches(data.branches)).catch(() => setBranches([]));
    api.get("/admin/dealers").then(({ data }) => setDealers(data)).catch(() => setDealers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDealer = async (branch, code) => {
    try {
      await api.put(`/hr/branches/${encodeURIComponent(branch.branch)}`, { dealer_code: code === "__none" ? null : code });
      toast.success(`${branch.display} → ${code === "__none" ? "no dealer" : code}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (branches.length === 0) return null;

  return (
    <div className="border bg-card rounded-md overflow-hidden" data-testid="branch-dealer-mapping">
      <div className="px-4 py-3 border-b">
        <h3 className="font-heading text-sm font-semibold">Branch → parent dealer</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Branches (from the HR sheet) inherit their market RTOs from the parent dealer's tags above — one mapping to maintain.</p>
      </div>
      <div className="max-h-[45vh] overflow-auto grid sm:grid-cols-2">
        {branches.map((b) => (
          <div key={b.branch} className="flex items-center justify-between gap-2 px-4 py-1.5 text-sm hover:bg-muted/50 border-b">
            <span className="font-medium truncate">{b.display}</span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-muted-foreground hidden md:inline">{b.rto_codes.length ? `${b.rto_codes.length} RTOs` : "—"}</span>
              <Select value={b.dealer_code || "__none"} onValueChange={(v) => setDealer(b, v)}>
                <SelectTrigger className="h-7 w-40 text-xs" data-testid={`branch-dealer-${b.branch}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No dealer</SelectItem>
                  {dealers.map((d) => <SelectItem key={d.code} value={d.code}>{d.code} · {d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ManpowerAdmin() {
  const [depts, setDepts] = useState([]);

  const load = useCallback(() => {
    api.get("/hr/departments").then(({ data }) => setDepts(data)).catch(() => setDepts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setCat = async (dept, category) => {
    try {
      await api.put("/hr/departments", { dept, category });
      toast.success(`${dept} → ${category}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (depts.length === 0)
    return <p className="text-sm text-muted-foreground">No manpower data yet — upload the HR master sheet on the Uploads screen first.</p>;

  return (
    <div className="border bg-card rounded-md overflow-hidden max-w-2xl">
      <div className="px-4 py-3 border-b">
        <h3 className="font-heading text-sm font-semibold">Department categories</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Sales roles see sales + common departments · Service roles see service + common · MD/Admin see everything. Branch → dealer mapping lives in the Dealers tab.</p>
      </div>
      <div className="max-h-[60vh] overflow-auto divide-y">
        {depts.map((d) => (
          <div key={d.dept} className="flex items-center justify-between px-4 py-1.5 text-sm hover:bg-muted/50">
            <span>{d.dept}</span>
            <Select value={d.category} onValueChange={(v) => setCat(d.dept, v)}>
              <SelectTrigger className="h-7 w-40 text-xs" data-testid={`dept-cat-${d.dept}`}><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}
